import React, { useEffect, useState } from "react"
import * as DateFns from "date-fns"
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/solid"
import { TrashIcon } from "@heroicons/react/24/outline"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faPenNib } from "@fortawesome/free-solid-svg-icons"

import { useNana } from "../../../hooks/useNana"
import {
  EditableProjectFields,
  ID,
  Idea,
  Project,
  ProjectStatus,
} from "../../../../model/types"
import IdeaBox, { GhostBox } from "../../../components/idea_box"
import * as Commands from "../../../../model/commands/index"
import EditProjectButton from "./edit_project_button"

export default function ProjectHeader({
  projectId,
  setOpenProjectId,
}: {
  projectId: ID
  setOpenProjectId: (id: ID | null) => void
}) {
  const { model, commander, hub } = useNana()
  const [project, setProject] = useState<Project | null>(null)

  const goal: Idea | null =
    project && project.goalId ? model.getIdea(project.goalId) : null

  const setProjectStatus = (newStatus: ProjectStatus) => {
    commander.exec(
      new Commands.SetProjectFieldsCommand({
        id: projectId,
        fields: {
          status: newStatus,
        },
      })
    )
  }

  useEffect(() => {
    const project = model.fetchProject(projectId)
    setProject(project)
  }, [projectId])

  useEffect(() => {
    const keys = [
      hub.on("model:project-updated", () =>
        setProject(model.fetchProject(projectId))
      ),
    ]

    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [projectId])

  const deleteProject = (project: Project) => {
    if (window.confirm("Are you sure?"))
      commander.exec(new Commands.DeleteProjectCommand({ id: project.id }))
  }

  if (!project) return null

  return (
    <div className="mb-2 p-4 md:p-8 " data-testid="project-header">
      <div className="mb-4 flex hidden-if-keyboard justify-between pb-4  border-b border-gray-200 dark:border-gray-800">
        <div className="flex gap-2 items-center">
          {goal && <IdeaBox idea={goal} />}
          {!goal && <GhostBox type={"goal"} />}
        </div>
        <div className="flex items-center gap-1">
          <EditProjectButton project={project} />
          {project.status == "in_progress" && (
            <button
              onClick={() => setProjectStatus("completed")}
              type="button"
              className="h-7 inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
              data-testid="mark-project-completed-button"
            >
              <CheckCircleIcon className="h-5 w-5 text-sky-500" />
              Mark completed
            </button>
          )}
          {project.status == "completed" && (
            <button
              onClick={() => setProjectStatus("in_progress")}
              type="button"
              className="h-7 inline-flex items-center gap-x-1.5 rounded-md px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
              data-testid="mark-project-in-progress-button"
            >
              Mark as in progress
            </button>
          )}
          <button
            onClick={() => {
              deleteProject(project)
              setOpenProjectId(null)
            }}
            className="h-7 inline-flex items-center rounded-md px-1.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
            data-testid="delete-project-button"
          >
            <TrashIcon className="h-4 w-4 text-sm" />
          </button>
          <button
            onClick={() => setOpenProjectId(null)}
            className="h-7 inline-flex items-center rounded-md px-1.5 py-1.5 text-xs text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700"
            data-testid="close-open-project-button"
          >
            <XMarkIcon className="h-4 w-4 text-sm" />
          </button>
        </div>
      </div>
      <div className="flex gap-4 justify-between">{project.name}</div>
      <div className="mt-4 md:mt-1 text-sm hidden md:flex hidden-if-keyboard gap-2 items-center">
        {project.createdAt ? (
          <div className="mt-1 ">{DateFns.format(project.createdAt, "MMM d, yyyy")}</div>
        ) : null}
        {project.completedAt && project.status == "completed" ? (
          <>
            <div className="mt-1 hidden md:block text-gray-600 text-lg">
              &bull;
            </div>
            <div className="mt-1">
              {DateFns.format(project.completedAt, "MMM d, yyyy")}
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}
